import type { ModuleWithProgress } from '../../hooks/useEducationModulesProgress';

type StepState = 'completed' | 'current' | 'upcoming';

type ModuleStepperProps = {
  modules: ModuleWithProgress[];
  activeIndex: number;
  isGated: boolean;
  onSelect: (index: number) => void;
};

function getStepState(
  module: ModuleWithProgress,
  index: number,
  activeIndex: number,
): StepState {
  // COMPLETED WINS OVER CURRENT: ON THE LAST MODULE, OR ANY RE-OPENED COMPLETED
  // MODULE, COMPLETION IS THE SIGNAL THE PATIENT NEEDS TO SEE.
  if (module.completed) return 'completed';
  if (index === activeIndex) return 'current';
  return 'upcoming';
}

function isSelectable(state: StepState, isGated: boolean): boolean {
  if (state === 'current') return true;
  if (!isGated) return true;
  if (state === 'completed') return true;
  return false;
}

const rowBase =
  'flex items-center gap-4 min-h-[64px] px-4 py-3 my-2 rounded-xl border-2 w-full text-left transition-all';

const rowByState: Record<StepState, string> = {
  current: 'bg-red-50 border-mideo-darkred border-[3px]',
  completed: 'bg-yellow-50 border-mideo-button-dark-green',
  upcoming: 'bg-white border-gray-300',
};

const rowDisabled = 'bg-gray-100 border-gray-200 cursor-not-allowed';

const badgeBase =
  'flex items-center justify-center w-11 h-11 rounded-full font-bold text-xl shrink-0';

const badgeByState: Record<StepState, string> = {
  current: 'bg-mideo-darkred text-white',
  completed: 'bg-mideo-button-dark-green text-white',
  upcoming: 'bg-gray-300 text-gray-700',
};

const badgeDisabled = 'bg-gray-400 text-gray-100';

const titleByState: Record<StepState, string> = {
  current: 'text-mideo-darkred font-bold',
  completed: 'text-gray-900 font-bold',
  upcoming: 'text-gray-900 font-bold',
};

const titleDisabled = 'text-gray-500';

export function ModuleStepper({
  modules,
  activeIndex,
  isGated,
  onSelect,
}: ModuleStepperProps) {
  return (
    <div className="my-4 w-full max-w-2xl">
      {modules.map((module, index) => {
        const state = getStepState(module, index, activeIndex);
        const isActive = index === activeIndex;
        const selectable = isSelectable(state, isGated);
        const stateLabel = isActive
          ? 'Now playing'
          : state === 'completed'
            ? 'Completed'
            : state === 'current'
              ? 'In progress'
              : 'Not started';

        return (
          <button
            key={module.videoId}
            type="button"
            onClick={() => selectable && onSelect(index)}
            disabled={!selectable}
            aria-current={state === 'current' ? 'step' : undefined}
            aria-label={`Module ${index + 1} of ${modules.length}: ${
              module.title
            }. ${stateLabel}.`}
            className={`${rowBase} ${
              selectable ? rowByState[state] : rowDisabled
            } ${selectable ? 'cursor-pointer hover:opacity-90' : ''}`}
          >
            <div
              className={`${badgeBase} ${
                selectable ? badgeByState[state] : badgeDisabled
              }`}
            >
              {state === 'completed' ? '✓' : index + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={`text-lg ${
                  selectable ? titleByState[state] : titleDisabled
                }`}
              >
                {module.title}
              </div>
              <div className="text-sm text-gray-600 mt-0.5">{stateLabel}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
